import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';
import { ConversionFamily, FirstPackageClientEntry, MonthlyConversion, PeriodKey } from '../types';

/** How a first package ended: the client bought another, used every session without buying, or stopped mid-package. */
export type ConversionSegment = 'converted' | 'finished' | 'dropped';

/** Bottom to top in the stacked bars, left to right in the legend, the meters and the table's chips. */
export const SEGMENT_ORDER: ConversionSegment[] = ['converted', 'finished', 'dropped'];

export const SEGMENT_LABELS: Record<ConversionSegment, string> = {
  converted: 'Converted',
  finished: 'Finished, did not rebuy',
  dropped: 'Dropped off mid-package',
};

export const SEGMENT_COLORS: Record<ConversionSegment, string> = {
  converted: '#2563EB',
  finished: '#EA580C',
  dropped: '#0D9488',
};

/** The unfilled track behind a conversion meter. */
export const CONVERSION_TRACK_COLOR = '#DBEAFE';

/** Days after a first package expires during which a client who has not rebought may still convert. */
export const MAY_STILL_CONVERT_DAYS = 30;

/** The entry's segment, from the outcome and the completed flag only; the session counts can disagree with the view and are never used. */
export function segmentOf(entry: FirstPackageClientEntry): ConversionSegment {
  if (entry.outcome === 'converted') return 'converted';
  return entry.completedAllSessions ? 'finished' : 'dropped';
}

export interface SegmentCounts {
  expired: number;
  converted: number;
  finished: number;
  dropped: number;
}

/** Entries of one period, counted by segment; `expired` is every entry in the period. */
export function countSegments(entries: FirstPackageClientEntry[], period: PeriodKey): SegmentCounts {
  const counts: SegmentCounts = { expired: 0, converted: 0, finished: 0, dropped: 0 };
  entries.forEach((entry) => {
    if (entry.period !== period) return;
    counts.expired += 1;
    counts[segmentOf(entry)] += 1;
  });
  return counts;
}

/** The last non-zero segment in stack order, the one drawn on top of a month's bar; null when all three are zero. */
export function topSegment(counts: Pick<SegmentCounts, 'converted' | 'finished' | 'dropped'>): ConversionSegment | null {
  return SEGMENT_ORDER.reduce<ConversionSegment | null>((top, key) => (counts[key] > 0 ? key : top), null);
}

export interface MonthlySegments extends MonthlyConversion {
  finished: number;
  dropped: number;
}

/**
 * The monthly series with each month's non-converted packages split into finished and dropped, counted from the
 * client list by expiry month. The monthly totals win: the split is clamped to `expired − converted`, finished
 * first, so a list that disagrees with the totals never overfills a month. Without a list both are 0.
 */
export function monthlySegments(conversion: ConversionFamily): MonthlySegments[] {
  return conversion.monthly.map((month) => {
    const segments = conversion.clients.filter((entry) => entry.expiryMonth === month.month).map(segmentOf);
    const available = Math.max(0, month.expired - month.converted);
    const finished = Math.min(segments.filter((segment) => segment === 'finished').length, available);
    const dropped = Math.min(segments.filter((segment) => segment === 'dropped').length, available - finished);
    return { ...month, finished, dropped };
  });
}

/**
 * Whether a client who has not converted may still do so: their first package expired 0 to `MAY_STILL_CONVERT_DAYS`
 * calendar days before the data's `sourceCreatedDate`, both ends inclusive. False when either date is missing or invalid.
 */
export function mayStillConvert(entry: FirstPackageClientEntry, sourceCreatedDate: string): boolean {
  if (entry.outcome === 'converted' || entry.firstExpiryDate === null) return false;
  const expiry = parseISO(entry.firstExpiryDate);
  const source = parseISO(sourceCreatedDate);
  if (!isValid(expiry) || !isValid(source)) return false;
  const days = differenceInCalendarDays(source, expiry);
  return days >= 0 && days <= MAY_STILL_CONVERT_DAYS;
}

const dayCount = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/** "renewed 19 days before expiry", "renewed on the expiry day", "13 days after expiry"; null when there was no next package. */
export function renewalText(days: number | null): string | null {
  if (days === null) return null;
  if (days < 0) return `renewed ${dayCount(-days)} before expiry`;
  if (days === 0) return 'renewed on the expiry day';
  return `${dayCount(days)} after expiry`;
}

/** Sessions left on the first package: the view's remaining count, else purchased − used when both are known, else null. */
export function unusedSessions(entry: FirstPackageClientEntry): number | null {
  const { sessionsRemaining, sessionsPurchased, sessionsUsed } = entry;
  if (sessionsRemaining !== null) return sessionsRemaining;
  if (sessionsPurchased !== null && sessionsUsed !== null) return sessionsPurchased - sessionsUsed;
  return null;
}
